import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchKiemKeList } from '@/apis/kiemKeApi'
import { fetchChuyenHangList } from '@/apis/chuyenHangApi'
import { fetchPhieuNhapKhoList } from '@/apis/phieuNhapKhoApi'
import { fetchPhieuXuatKhoList } from '@/apis/phieuXuatKhoApi'
import { useMenuChucNang } from '@/hooks/useMenuChucNang'
import { KIEM_KE_PERMISSION_CODES } from '@/constants/chucNangs/kiemKeChucNangs'
import { CHUYEN_HANG_PERMISSION_CODES } from '@/constants/chucNangs/chuyenHangChucNangs'
import { PHIEU_NHAP_KHO_PERMISSION_CODES } from '@/constants/chucNangs/phieuNhapKhoChucNangs'
import { PHIEU_XUAT_KHO_PERMISSION_CODES } from '@/constants/chucNangs/phieuXuatKhoChucNangs'
import { primaryCodeFromRow } from '@/utils/banHangListDisplayUtils'

export type PhieuTamTonKhoItem = {
  id: string
  code: string
}

export type PhieuTamTonKhoGroup = {
  total: number
  items: PhieuTamTonKhoItem[]
}

const EMPTY_GROUP: PhieuTamTonKhoGroup = { total: 0, items: [] }
const PREVIEW_SIZE = 8
const POLL_MS = 60_000

const mapPreview = (rows: Record<string, unknown>[]): PhieuTamTonKhoItem[] =>
  rows
    .map((row) => {
      const id = String(row.id ?? '').trim()
      if (!id) return null
      return { id, code: primaryCodeFromRow(row) || id }
    })
    .filter((x): x is PhieuTamTonKhoItem => x != null)

/**
 * Đếm phiếu kiểm kê / chuyển hàng / nhập / xuất trạng thái `phieuTam` — nhắc hoàn thành để trừ/cộng tồn.
 */
export const usePhieuTamTonKhoAlert = () => {
  const { hasAnyChucNangByCode } = useMenuChucNang()
  const canKiemKe = hasAnyChucNangByCode([KIEM_KE_PERMISSION_CODES.WEB_BH_KIEM_KE_LIST])
  const canChuyenHang = hasAnyChucNangByCode([CHUYEN_HANG_PERMISSION_CODES.WEB_BH_CHUYEN_HANG_LIST])
  const canNhapKho = hasAnyChucNangByCode([PHIEU_NHAP_KHO_PERMISSION_CODES.WEB_BH_PHIEU_NHAP_KHO_LIST])
  const canXuatKho = hasAnyChucNangByCode([PHIEU_XUAT_KHO_PERMISSION_CODES.WEB_BH_PHIEU_XUAT_KHO_LIST])
  const [kiemKe, setKiemKe] = useState<PhieuTamTonKhoGroup>(EMPTY_GROUP)
  const [chuyenHang, setChuyenHang] = useState<PhieuTamTonKhoGroup>(EMPTY_GROUP)
  const [nhapKho, setNhapKho] = useState<PhieuTamTonKhoGroup>(EMPTY_GROUP)
  const [xuatKho, setXuatKho] = useState<PhieuTamTonKhoGroup>(EMPTY_GROUP)
  const [loading, setLoading] = useState(false)
  const alive = useRef(true)

  const reload = useCallback(async () => {
    if (!canKiemKe && !canChuyenHang && !canNhapKho && !canXuatKho) {
      setKiemKe(EMPTY_GROUP)
      setChuyenHang(EMPTY_GROUP)
      setNhapKho(EMPTY_GROUP)
      setXuatKho(EMPTY_GROUP)
      return
    }
    setLoading(true)
    try {
      const qs = `?page=1&pageSize=${PREVIEW_SIZE}&trangThai=phieuTam`
      const [kk, ch, nk, xk] = await Promise.all([
        canKiemKe
          ? fetchKiemKeList(qs).then((res) => ({
              total: res.paging?.total ?? 0,
              items: mapPreview(
                (res.data ?? []).map((r) => ({
                  id: r.id,
                  maPhieuKiem: r.maPhieuKiem,
                  code: r.maPhieuKiem,
                })) as unknown as Record<string, unknown>[],
              ),
            }))
          : Promise.resolve(EMPTY_GROUP),
        canChuyenHang
          ? fetchChuyenHangList(qs).then((res) => ({
              total: res.paging?.total ?? 0,
              items: mapPreview(
                (res.data ?? []).map((r) => ({
                  id: r.id,
                  maPhieuChuyen: r.maPhieuChuyen ?? r.maPhieu,
                })) as Record<string, unknown>[],
              ),
            }))
          : Promise.resolve(EMPTY_GROUP),
        canNhapKho
          ? fetchPhieuNhapKhoList(qs).then((res) => ({
              total: res.paging?.total ?? 0,
              items: mapPreview(
                (res.data ?? []).map((r) => ({
                  id: r.id,
                  maPhieu: r.maPhieu,
                })) as unknown as Record<string, unknown>[],
              ),
            }))
          : Promise.resolve(EMPTY_GROUP),
        canXuatKho
          ? fetchPhieuXuatKhoList(qs).then((res) => ({
              total: res.paging?.total ?? 0,
              items: mapPreview(
                (res.data ?? []).map((r) => ({
                  id: r.id,
                  maPhieu: r.maPhieu,
                })) as unknown as Record<string, unknown>[],
              ),
            }))
          : Promise.resolve(EMPTY_GROUP),
      ])
      if (!alive.current) return
      setKiemKe(kk)
      setChuyenHang(ch)
      setNhapKho(nk)
      setXuatKho(xk)
    } catch {
      if (!alive.current) return
      /* giữ số cũ — tránh nhấp nháy 0 khi lỗi mạng */
    } finally {
      if (alive.current) setLoading(false)
    }
  }, [canChuyenHang, canKiemKe, canNhapKho, canXuatKho])

  useEffect(() => {
    alive.current = true
    void reload()
    const timer = window.setInterval(() => void reload(), POLL_MS)
    const onFocus = () => void reload()
    window.addEventListener('focus', onFocus)
    return () => {
      alive.current = false
      window.clearInterval(timer)
      window.removeEventListener('focus', onFocus)
    }
  }, [reload])

  const totalCount = kiemKe.total + chuyenHang.total + nhapKho.total + xuatKho.total

  return {
    kiemKe,
    chuyenHang,
    nhapKho,
    xuatKho,
    totalCount,
    loading,
    canKiemKe,
    canChuyenHang,
    canNhapKho,
    canXuatKho,
    reload,
  }
}
